import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
    Modal,
    Button,
    Radio,
    Select,
    Checkbox,
    Typography,
    Divider,
    Tag,
    Table,
    Input,
    message,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { formatDateWithSuffix } from "../../icons/ApplicationColor";
import useCommonListing from "../customHooks/useCommonListing";
import { GetFormattedDateTime } from "../../utils/DateTimeUtils";
import axiosInstance from "../../../api/Axios";

const { Text, Title } = Typography;
const { Option } = Select;

const MONTHS = [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
];

const getDaysInMonth = (month) => {
    if (!month) return 31;
    const days = new Date(2024, month, 0).getDate();
    return days;
};

const BundleResetModal = ({ open, onClose, data1, onApply }) => {
    const userInfo = useSelector((state) => state.user);
    const [resetFactor, setResetFactor] = useState("Static Date");
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [enableRecurring, setEnableRecurring] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Reset all state when modal closes
    useEffect(() => {
        if (!open) {
            setResetFactor("Static Date");
            setSelectedMonth(null);
            setSelectedDay(null);
            setEnableRecurring(false);
            setSelectedRowKeys([]);
        }
    }, [open]);

    const bundleId = data1?.id || data1?.bundle_id;

    const {
        data: records,
        itemsLength,
        loading: loadingRecords,
        filterData: recordsFilterData,
        onPageChange: onRecordsPageChange,
        onShowSizeChange: onRecordsSizeChange,
        handleSearchChange: handleRecordsSearchChange,
    } = useCommonListing({
        userInfo,
        passSize: 5,
        passPage: 1,
        passURL: `learn/enrollments/`,
        loadAgain: open,
        passFilterUrl: `course_id=${bundleId}&type=bundle&organization_id=${userInfo?.data?.organization?.id || data1?.organization_id}`,
        includeStatus: false,
        skip: !open || !bundleId,
    });

    const recordColumns = [
        {
            title: "Name",
            dataIndex: "user_name",
            key: "user_name",
            render: (text) => <Text>{text || "—"}</Text>
        },
        {
            title: "Courses",
            dataIndex: "course_name",
            key: "course_name",
            width: 300,
            render: (text) => <Text style={{ fontSize: 13 }}>{text || "—"}</Text>
        },
        {
            title: "Registered on",
            dataIndex: "enrollment_date",
            key: "enrollment_date",
            render: (date) => GetFormattedDateTime(date)
        },
        {
            title: "Completed On",
            dataIndex: "completion_date",
            key: "completion_date",
            render: (date) => GetFormattedDateTime(date)
        },
        {
            title: "Status",
            dataIndex: "progress_status",
            key: "progress_status",
            render: (status) => {
                const colors = {
                    "Enrolled": "blue",
                    "Started": "orange",
                    "Completed": "green",
                    "Cancelled": "red",
                    "Active": "green"
                };
                return <Tag color={colors[status] || "default"} style={{ borderRadius: 12 }}>{status}</Tag>;
            }
        }
    ];

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const handleClear = () => {
        setResetFactor("Static Date");
        setSelectedMonth(null);
        setSelectedDay(null);
        setEnableRecurring(false);
        setSelectedRowKeys([]);
    };

    const handleApply = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one record to reset.");
            return;
        }

        if (!selectedMonth || !selectedDay) {
            message.error("Please select a valid Month and Day for the schedule.");
            return;
        }

        function getCookie(name) {
            const cookies = document.cookie.split("; ");
            for (let i = 0; i < cookies.length; i++) {
                const [cookieName, cookieValue] = cookies[i].split("=");
                if (cookieName === name) {
                    return cookieValue;
                }
            }
            return null;
        }

        const csrfToken = getCookie("csrftoken");

        setSubmitting(true);
        try {
            const payload = {
                organization_id: userInfo?.data?.organization?.id || data1?.organization_id,
                tenant_id: userInfo?.data?.organization?.tenant_id || data1?.tenant_id || null,
                enrollment_ids: selectedRowKeys.map((id) => String(id)),
                reset_type: "scheduled",
                recurring: enableRecurring,
                reset_factor: "static_date",
                frequency: null,
                static_date: {
                    month: selectedMonth,
                    day: selectedDay,
                    selected_date: new Date().toISOString(),
                },
                created_by_id: String(userInfo?.data?.id),
            };

            await axiosInstance.post("learn/reset/", payload, {
                headers: {
                    "X-CSRFToken": csrfToken,
                },
            });
            message.success("Bundle reset configuration applied successfully.");
            if (onApply) {
                onApply(payload);
            }
            onClose();
        } catch (error) {
            console.error("Error resetting bundle:", error);
            message.error(error?.response?.data?.message || "Failed to apply reset configuration.");
        } finally {
            setSubmitting(false);
        }
    };

    const formatResetDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        if (isNaN(date)) return "—";
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const day = date.getUTCDate();
        const month = monthNames[date.getUTCMonth()];
        const year = date.getUTCFullYear();
        return `${month}-${day < 10 ? "0" + day : day}-${year}`;
    };

    const dayOptions = Array.from({ length: getDaysInMonth(selectedMonth) }, (_, i) => i + 1);

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={
                <span style={{ fontWeight: 700, fontSize: 18 }}>Reset Bundle</span>
            }
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                    <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button
                        type="primary"
                        style={{ background: "#254061", borderColor: "#254061" }}
                        onClick={handleApply}
                        loading={submitting}
                    >
                        Schedule Reset {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}
                    </Button>
                </div>
            }
            width={1200}
            styles={{ body: { padding: "20px 0 8px 0" } }}
            destroyOnClose
        >
            <div style={{ display: "flex", minHeight: 420, gap: 0 }}>

                {/* ──────────── LEFT: Reset Settings ──────────── */}
                <div style={{ width: 300, minWidth: 260, padding: "0 20px 20px 20px" }}>
                    {/* Bordered card */}
                    <div
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 8,
                            padding: "18px 20px 14px 20px",
                            background: "#fff",
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                        }}
                    >
                        <Text strong style={{ fontSize: 15 }}>
                            Schedule Reset
                        </Text>
                        <Divider style={{ margin: "0 0 4px 0" }} />

                        {/* Reset Factor */}
                        <div style={{ flexDirection: "column", gap: 4 }}>
                            <Text style={{ color: "#888", fontSize: 12.5 }}>Reset Factor</Text>
                            <Select
                                value={resetFactor}
                                onChange={setResetFactor}
                                style={{ width: "100%" }}
                                size="middle"
                            >
                                <Option value="Static Date">Static Date</Option>
                            </Select>
                        </div>

                        {/* Static Date – Month + Day */}
                        <div style={{ flexDirection: "column", gap: 4 }}>
                            <Text style={{ color: "#888", fontSize: 12.5 }}>Static Date</Text>
                            <div style={{ display: "flex", gap: 8 }}>
                                <Select
                                    placeholder="Month"
                                    value={selectedMonth}
                                    onChange={(val) => {
                                        setSelectedMonth(val);
                                        setSelectedDay(null); // reset day when month changes
                                    }}
                                    style={{ flex: 1 }}
                                    size="middle"
                                >
                                    {MONTHS.map((m) => (
                                        <Option key={m.value} value={m.value}>
                                            {m.label}
                                        </Option>
                                    ))}
                                </Select>
                                <Select
                                    placeholder="Day"
                                    value={selectedDay}
                                    onChange={setSelectedDay}
                                    style={{ flex: 1 }}
                                    size="middle"
                                    disabled={!selectedMonth}
                                >
                                    {dayOptions.map((d) => (
                                        <Option key={d} value={d}>
                                            {d}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        {/* Enable Recurring Reset */}
                        <div>
                            <Checkbox
                                checked={enableRecurring}
                                onChange={(e) => setEnableRecurring(e.target.checked)}
                                style={{ color: "#555" }}
                            >
                                Enable Recurring Reset
                            </Checkbox>
                        </div>

                        {/* Clear / Apply */}
                        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                            <Button
                                style={{ flex: 1, background: "#6c757d", color: "#fff", border: "none" }}
                                onClick={handleClear}
                            >
                                Clear
                            </Button>
                            <Button
                                type="primary"
                                style={{ flex: 1, background: "#254061", borderColor: "#254061" }}
                                onClick={handleApply}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Vertical divider */}
                <div style={{ width: 1, background: "#f0f0f0", margin: "0 4px" }} />

                {/* ──────────── RIGHT: Bundle Details & Records ──────────── */}
                <div
                    style={{
                        flex: 1,
                        padding: "0 24px 20px 20px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0,
                        overflow: "hidden",
                        minWidth: 0,
                    }}
                >
                    <Title level={5} style={{ marginBottom: 8 }}>
                        Bundle Details
                    </Title>
                    <Divider style={{ margin: "8px 0 16px" }} />

                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Text style={{ color: "gray", minWidth: 110 }}>Name :</Text>
                            <Text strong>{data1?.name || "—"}</Text>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Text style={{ color: "gray", minWidth: 110 }}>Bundle ID :</Text>
                            <Text>{data1?.bundle_id || "—"}</Text>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Text style={{ color: "gray", minWidth: 110 }}>Due Date :</Text>
                            <Text>{formatDateWithSuffix(data1?.due_date) || "—"}</Text>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                            <Text style={{ color: "gray", minWidth: 110 }}>Deactivation :</Text>
                            <Text>{formatDateWithSuffix(data1?.deactivate_on) || "—"}</Text>
                        </div>
                    </div>

                    <Divider style={{ margin: "16px 0" }} />

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, marginTop: 8 }}>
                        <Title level={5} style={{ margin: 0 }}>
                            Records | <span style={{ color: "gray", fontWeight: 400 }}>({itemsLength || 0})</span>
                        </Title>
                        <Input
                            placeholder="Search..."
                            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                            style={{ width: 220, borderRadius: 8 }}
                            onChange={handleRecordsSearchChange}
                            value={recordsFilterData?.search}
                        />
                    </div>
                    <Table
                        rowSelection={rowSelection}
                        columns={recordColumns}
                        dataSource={records}
                        loading={loadingRecords}
                        pagination={{
                            current: recordsFilterData?.currentPage,
                            pageSize: recordsFilterData?.pageSize,
                            total: itemsLength,
                            onChange: onRecordsPageChange,
                            onShowSizeChange: onRecordsSizeChange,
                            size: "small",
                            showSizeChanger: true,
                        }}
                        size="small"
                        rowKey={(record) => record.enrollment_id || record.id}
                        bordered
                        scroll={{ x: "max-content" }}
                    />

                    {Array.isArray(data1?.tags) && data1.tags.length > 0 && (
                        <>
                            <Divider style={{ margin: "16px 0" }} />
                            <Text strong style={{ fontSize: 14, color: "#555", marginBottom: 10, display: "block" }}>
                                Tags
                            </Text>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                {data1.tags.map((tag) => (
                                    <Tag key={tag} color="geekblue" style={{ borderRadius: 12, padding: "3px 10px" }}>
                                        {tag}
                                    </Tag>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default BundleResetModal;
